import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Copy,
  ExternalLink,
  Loader2,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type SchedulingLink = {
  id: string;
  url: string;
  durationMinutes: number | null;
  isDefault: boolean;
};

type Person = {
  id: string;
  fullName: string;
  schedulingLinks: SchedulingLink[];
  corporateEmail: string | null;
};

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "60 min" },
  { value: "90", label: "90 min" },
  { value: "120", label: "120 min" },
];

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getDurationLabel(minutes: number | null) {
  if (!minutes) return "Padrão";
  return `${minutes} min`;
}

function AdminAddLinkDialog({
  people,
  open,
  onOpenChange,
}: {
  people: Person[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [url, setUrl] = useState("");
  const [duration, setDuration] = useState("");

  const createMutation = useMutation({
    mutationFn: async (data: {
      employeeId: string;
      url: string;
      durationMinutes: number | null;
      isDefault: boolean;
    }) => {
      return rpcClient.profile.adminCreateSchedulingLink(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      onOpenChange(false);
      setSelectedEmployeeId("");
      setUrl("");
      setDuration("");
      toast({ title: "Link adicionado" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Falha ao adicionar link", variant: "destructive" });
    },
  });

  const handleSubmit = () => {
    if (!selectedEmployeeId || !url.trim()) return;
    const selected = people.find((p) => p.id === selectedEmployeeId);
    const isDefault = !selected || selected.schedulingLinks.length === 0;
    createMutation.mutate({
      employeeId: selectedEmployeeId,
      url: url.trim(),
      durationMinutes: duration ? Number.parseInt(duration, 10) : null,
      isDefault,
    });
  };

  const employeeOptions = people.map((p) => ({
    value: p.id,
    label: p.fullName,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-admin-add-link">
        <DialogHeader>
          <DialogTitle>Adicionar Link de Agendamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Colaborador</Label>
            <SearchableSelect
              value={selectedEmployeeId}
              onValueChange={setSelectedEmployeeId}
              options={employeeOptions}
              placeholder="Selecione um colaborador..."
              searchPlaceholder="Buscar por nome..."
              data-testid="select-employee"
            />
          </div>
          <div className="space-y-2">
            <Label>URL do link de agendamento</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://calendly.com/..."
              data-testid="input-link-url"
            />
          </div>
          <div className="space-y-2">
            <Label>Duração (opcional)</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger data-testid="select-duration">
                <SelectValue placeholder="Padrão (qualquer duração)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Padrão (qualquer duração)</SelectItem>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedEmployeeId || !url.trim() || createMutation.isPending}
            data-testid="button-submit-add-link"
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdminEditLinkDialog({
  link,
  employeeId,
  open,
  onOpenChange,
}: {
  link: SchedulingLink;
  employeeId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const [url, setUrl] = useState(link.url);
  const [duration, setDuration] = useState(
    link.durationMinutes ? String(link.durationMinutes) : "",
  );

  const updateMutation = useMutation({
    mutationFn: async (data: { url: string; durationMinutes: number | null }) => {
      return rpcClient.profile.adminUpdateSchedulingLink({
        employeeId,
        linkId: link.id,
        ...data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      onOpenChange(false);
      toast({ title: "Link atualizado" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Falha ao atualizar link", variant: "destructive" });
    },
  });

  const handleSubmit = () => {
    if (!url.trim()) return;
    updateMutation.mutate({
      url: url.trim(),
      durationMinutes: duration && duration !== "none" ? Number.parseInt(duration, 10) : null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-admin-edit-link">
        <DialogHeader>
          <DialogTitle>Editar Link de Agendamento</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>URL do link de agendamento</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://calendly.com/..."
              data-testid="input-edit-link-url"
            />
          </div>
          <div className="space-y-2">
            <Label>Duração (opcional)</Label>
            <Select value={duration || "none"} onValueChange={setDuration}>
              <SelectTrigger data-testid="select-edit-duration">
                <SelectValue placeholder="Padrão (qualquer duração)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Padrão (qualquer duração)</SelectItem>
                {DURATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!url.trim() || updateMutation.isPending}
            data-testid="button-submit-edit-link"
          >
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SchedulingLinksPage() {
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const canManageSchedulingLinks = hasPermission("recruiting");
  const [search, setSearch] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState<{
    link: SchedulingLink;
    employeeId: string;
  } | null>(null);

  const { data: people = [], isLoading } = useQuery(orpc.dashboard.people.queryOptions());

  const deleteMutation = useMutation({
    mutationFn: async ({ employeeId, linkId }: { employeeId: string; linkId: string }) => {
      await rpcClient.profile.adminDeleteSchedulingLink({ employeeId, linkId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      toast({ title: "Link removido" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Falha ao remover link", variant: "destructive" });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async ({ employeeId, linkId }: { employeeId: string; linkId: string }) => {
      return rpcClient.profile.adminUpdateSchedulingLink({ employeeId, linkId, isDefault: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.dashboard.people.queryOptions().queryKey });
      toast({ title: "Link padrão atualizado" });
    },
    onError: () => {
      toast({ title: "Erro", description: "Falha ao definir link padrão", variant: "destructive" });
    },
  });

  const peopleWithLinks = people.filter((p) => p.schedulingLinks.length > 0);

  const filtered = peopleWithLinks.filter(
    (p) =>
      p.fullName.toLowerCase().includes(search.toLowerCase()) ||
      p.corporateEmail?.toLowerCase().includes(search.toLowerCase()),
  );

  const copyToClipboard = (url: string, name: string) => {
    navigator.clipboard.writeText(url);
    toast({
      title: "Link copiado",
      description: `Link de ${name} copiado para a área de transferência.`,
    });
  };

  return (
    <div className="flex flex-col gap-4" data-testid="page-scheduling-links">
      <PageHeader
        title="Links de Agendamento"
        description="Links de agendamento dos recrutadores e entrevistadores da plataforma"
        icon={<Calendar className="h-5 w-5 text-primary" />}
      />

      <div className="flex items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-scheduling-links"
          />
        </div>
        {canManageSchedulingLinks && (
          <Button onClick={() => setAddDialogOpen(true)} data-testid="button-add-scheduling-link">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Link
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="text-empty-state">
          {search
            ? "Nenhum resultado encontrado para a busca."
            : "Nenhum colaborador possui links de agendamento configurados."}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((person) => (
            <div
              key={person.id}
              className="rounded-lg border bg-card p-4 space-y-3"
              data-testid={`card-person-${person.id}`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                    {getInitials(person.fullName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm font-medium truncate"
                    data-testid={`text-person-name-${person.id}`}
                  >
                    {person.fullName}
                  </p>
                  {person.corporateEmail && (
                    <p className="text-xs text-muted-foreground truncate">
                      {person.corporateEmail}
                    </p>
                  )}
                </div>
                {canManageSchedulingLinks && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setAddDialogOpen(true)}
                    data-testid={`button-add-link-${person.id}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {person.schedulingLinks.map((link) => (
                  <div
                    key={link.id}
                    className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2"
                    data-testid={`scheduling-link-${link.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-medium text-muted-foreground">
                          {getDurationLabel(link.durationMinutes)}
                        </span>
                        {link.isDefault && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            padrão
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{link.url}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {canManageSchedulingLinks && !link.isDefault && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() =>
                            setDefaultMutation.mutate({
                              employeeId: person.id,
                              linkId: link.id,
                            })
                          }
                          title="Definir como padrão"
                          data-testid={`button-set-default-${link.id}`}
                        >
                          <Star className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {canManageSchedulingLinks && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setEditingLink({ link, employeeId: person.id })}
                          data-testid={`button-edit-link-${link.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => copyToClipboard(link.url, person.fullName)}
                        data-testid={`button-copy-link-${link.id}`}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`button-open-link-${link.id}`}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                      {canManageSchedulingLinks && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() =>
                            deleteMutation.mutate({ employeeId: person.id, linkId: link.id })
                          }
                          data-testid={`button-delete-link-${link.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {canManageSchedulingLinks && (
        <AdminAddLinkDialog people={people} open={addDialogOpen} onOpenChange={setAddDialogOpen} />
      )}

      {canManageSchedulingLinks && editingLink && (
        <AdminEditLinkDialog
          link={editingLink.link}
          employeeId={editingLink.employeeId}
          open={true}
          onOpenChange={(v) => {
            if (!v) setEditingLink(null);
          }}
        />
      )}
    </div>
  );
}
